import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { Button, useBoolean } from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';
import { Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';

import { extractAccessToken, nameof } from 'common/helpers';

import {
    GeneralInfo,
    OTPType,
    DigitalSignInfo,
    SignType,
    NationInfo,
    AddressInfo,
    WorkingPosition
} from './components';
import useProfileViewModel from './Profile.viewmodel';
import Camera from './components/Camera';
import Swal from 'sweetalert2';
import { useChangePassword } from './queries';
import { useHistory } from 'react-router';
import { UserCredentialFormModal } from '../../presentations/view/UserAuthentication/components/UserCredentialFormModal';
import { ChangePasswordResponseModal } from '../../presentations/view/UserAuthentication/components/ChangePasswordResponseModal';
import { UserCompany } from '../../models/user/UserCompany';
import { F1ResetPasswordResponse } from "../../models/authentication/F1ResetPasswordResponse";
import { SelectCompanyModal } from './components';

function Profile() {
    const {
        isSCG,
        citizenId,
        previewImageBlobUrl,
        previewImageSignatureBlobUrl,
        userInformation,
        copyUserInformation,
        companySystemMaster,
        handleRemoveAttachFile,
        handleChangeCopyUserInformation,
        handleChangeOtpOrNotificationType,
        handleChangeAttachFile,
        handleAttachFile,
        handleSaveChange
    } = useProfileViewModel();

    const history = useHistory();

    const { mutate: changePassword } = useChangePassword();

    const profileImageInputRef = useRef<HTMLInputElement>(null);
    const [showCameraModal, setShowCameraModal] = useBoolean();

    const [selectedUser, setSelectedUser] = useState<UserCompany>();
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [f1ResetPasswordResponse, setF1ResetPasswordResponse] = useState<F1ResetPasswordResponse[]>([]);

    const [showSelectCompanyModal, setShowSelectCompanyModal] = useState(false);

    const handleClickChooseImage = useCallback(() => {
        profileImageInputRef.current?.click();
    }, []);

    useEffect(() => {
        const user: UserCompany = {
            citizen_id: citizenId,
            sid: '',
            company_name: '',
            email: '',
            first_name: '',
            last_name: '',
            workflow_status: '',
            workflow_email: '',
            dms_status: '',
            f1_employee_code: '',
            f1_username: '',
            f1_user_id: '',
            f1_first_name: '',
            f1_last_name: '',
            f1_first_name_th: '',
            f1_last_name_th: '',
            f1_email: '',
            system_type: '',
            type: '',
            status: '',
            welcome_email_sent: false,
            created_by: '',
            created_on: '',
            updated_by: '',
            updated_on: '',
            wf_first_name: '',
            wf_last_name: '',
            nationality: '',
            th_first_name: '',
            th_last_name: ''
        }

        console.log(userInformation)
        console.log(copyUserInformation)

        setSelectedUser(user)
    }, []);

    const handleClickChooseFile = useCallback(
        (file: File) => {
            handleAttachFile('image', file);
            setShowCameraModal.off();
        },
        [handleAttachFile]
    );

    const handleChangePasswordClick = async () => {
        const result = await Swal.fire({
            title: 'Change Password?',
            text: "This action will send change password email to user's email and this will logging out after this action.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, change it!'
        });
        if (!result.isConfirmed) {
            return;
        }

        const requestBody = { email: extractAccessToken().email };
        changePassword(requestBody, {
            onSuccess: async () => {
                localStorage.removeItem('SSO_APPLICATION');

                await Swal.fire('Success', 'You can check your email', 'success');
                history.replace('/logout');
            },
            onError: async error => {
                await Swal.fire(
                    'Error',
                    error.response?.data?.message ?? 'Something went wrong',
                    'error'
                );
            }
        });
    };

    const handleCloseUserCredentialFormModal = () => {
        setShowChangePasswordModal(false)
    };

    const handleCloseChangePasswordResponseModal = () => {
        setF1ResetPasswordResponse([]);
    }

    const validateBeforeSaveChange = () => {
        if (previewImageSignatureBlobUrl) {
            setShowSelectCompanyModal(true)

            return
        }

        handleSaveChange()
    }

    const handleCloseSelectCompanyModal = () => {
        setShowSelectCompanyModal(false)
    }

    const handleConfirmSelectCompanyModal = () => {
        setShowSelectCompanyModal(false)

        handleSaveChange()
    }

    return (
        <div className="flex flex-col gap-y-2.5 space-y-2 px-3 pb-4">
            <div className="flex flex-row">
                <p className="text-2xl font-bold">Profile</p>

                <div className="ms-auto flex gap-4">
                    <Button
                        size="md"
                        onClick={validateBeforeSaveChange}
                        className="bg-primary-900 text-xs text-white hover:bg-primary-700"
                    >
                        Save Change
                    </Button>

                    {!isSCG ? (
                        <Button
                            size="md"
                            onClick={handleChangePasswordClick}
                            className="bg-primary-900 text-xs text-white hover:bg-primary-700"
                        >
                            Change Password
                        </Button>
                    ) : (
                        <Button
                            size="md"
                            onClick={() => setShowChangePasswordModal(true)}
                            className="bg-primary-900 text-xs text-white hover:bg-primary-700"
                        >
                            Change Password
                        </Button>
                    )}
                </div>
            </div>

            <hr />

            {userInformation && copyUserInformation && (
                <div className="flex flex-col gap-4 xl:flex-row">
                    <div className="flex flex-col space-y-4">
                        <div className="relative h-[150px] w-[150px] overflow-hidden rounded-full border">
                            <FaTimes
                                size={16}
                                className="absolute -right-2 -top-2 box-content cursor-pointer rounded-full bg-red-600 p-0.5 text-white"
                                onClick={() => handleRemoveAttachFile('image')}
                            />
                            <img
                                src={previewImageBlobUrl ?? userInformation.image ?? ''}
                                className="h-full w-full object-cover"
                            />
                        </div>
                        <Menu>
                            <MenuButton
                                as={Button}
                                className="top-15 absolute left-36 m-1 rounded-full bg-white bg-opacity-75 p-2 text-gray-600"
                            >
                                <EditIcon boxSize={6} />
                            </MenuButton>
                            <MenuList>
                                <MenuItem onClick={handleClickChooseImage}>Choose File</MenuItem>
                                <MenuItem onClick={setShowCameraModal.on}>Camera</MenuItem>
                            </MenuList>
                        </Menu>
                        <input
                            ref={profileImageInputRef}
                            type="file"
                            name={nameof('image', userInformation)}
                            accept="image/png, image/jpg, image/jpeg"
                            onChange={handleChangeAttachFile}
                            className="hidden"
                        />
                        <Camera
                            isOpen={showCameraModal}
                            onClose={setShowCameraModal.off}
                            onClickChooseFile={handleClickChooseFile}
                        />

                        <div>
                            <p>{userInformation.employment_id ?? ''}</p>
                        </div>
                    </div>

                    <div className="flex-1 space-y-4">
                        <GeneralInfo // Bound
                            isSCG={isSCG}
                            userInformation={userInformation}
                            copyUserInformation={copyUserInformation}
                            handleChangeCopyUserInformation={handleChangeCopyUserInformation}
                        />
                        <OTPType // Bound
                            userInformation={userInformation}
                            copyUserInformation={copyUserInformation}
                            onChangeOtpOrNotificationType={handleChangeOtpOrNotificationType}
                        />

                        {!isSCG ? (
                            <React.Fragment>
                                <DigitalSignInfo
                                    previewImageSignatureBlobUrl={previewImageSignatureBlobUrl}
                                    userInformation={userInformation}
                                    copyUserInformation={copyUserInformation}
                                    handleChangeAttachFile={handleChangeAttachFile}
                                    handleChangeCopyUserInformation={
                                        handleChangeCopyUserInformation
                                    }
                                    handleRemoveAttachFile={handleRemoveAttachFile}
                                />
                                <SignType
                                    userInformation={userInformation}
                                    copyUserInformation={copyUserInformation}
                                    handleChangeCopyUserInformation={
                                        handleChangeCopyUserInformation
                                    }
                                />
                                <NationInfo // Bound
                                    userInformation={userInformation}
                                    copyUserInformation={copyUserInformation}
                                    handleChangeCopyUserInformation={
                                        handleChangeCopyUserInformation
                                    }
                                />
                                <AddressInfo // Bound
                                    userInformation={userInformation}
                                    copyUserInformation={copyUserInformation}
                                    handleChangeCopyUserInformation={
                                        handleChangeCopyUserInformation
                                    }
                                />
                                <WorkingPosition // Bound
                                    userInformation={userInformation}
                                    copyUserInformation={copyUserInformation}
                                    handleChangeCopyUserInformation={
                                        handleChangeCopyUserInformation
                                    }
                                />
                                <SelectCompanyModal
                                    companySystemMaster={companySystemMaster}
                                    isOpen={showSelectCompanyModal}
                                    onClose={handleCloseSelectCompanyModal}
                                    onConfirm={handleConfirmSelectCompanyModal}
                                />
                            </React.Fragment>
                        ) : (
                            <>
                                <UserCredentialFormModal
                                    isSCG={isSCG}
                                    user={selectedUser}
                                    isOpen={showChangePasswordModal}
                                    requiredOldPassword={true}
                                    onClose={handleCloseUserCredentialFormModal}
                                    onF1ResetPasswordResponseChange={setF1ResetPasswordResponse}
                                />
                                <ChangePasswordResponseModal
                                    isOpen={(f1ResetPasswordResponse.length > 0)}
                                    f1ResetPasswordResponse={f1ResetPasswordResponse}
                                    onClose={handleCloseChangePasswordResponseModal}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Profile;
